// Карточка созданного документа. SMY-111, переселена в ленту в SMY-114
//
// ГЛАВНОЕ ЗДЕСЬ — не пункты, а СКОБКИ. Единица обещает шаблон, а не готовый
// документ, и список того, что надо подставить самому, это и есть граница
// между ними. Спрятать его значит выдать шаблон за готовое — поэтому он
// стоит открытым блоком, а не за раскрытием.

import { useState } from 'react'
import { DOC_TEMPLATE_LABELS, type DocDraft as DocDraftPayload, type DocTemplate } from '@smyslika/core'

import { useSession } from '../session.tsx'
import { Caps, Card, Divider } from '../ui/index.ts'
import { CardHead, CardNote, openFile, type CardProps } from './common.tsx'

export function DocDraftCard({ artifact, charged, unit, runId }: CardProps) {
  const { artifacts } = useSession()
  const [note, setNote] = useState<string | null>(null)
  const [downloading, setDownloading] = useState<'docx' | 'pdf' | null>(null)

  const card = artifact.payload as DocDraftPayload
  const kind = DOC_TEMPLATE_LABELS[card.template as DocTemplate] ?? 'Документ'

  const download = (format: 'docx' | 'pdf') => (): void => {
    setDownloading(format)
    setNote(null)
    artifacts
      .export(artifact.id, format)
      .then((f) => openFile(f.url))
      .catch((e: Error) => setNote(e.message))
      .finally(() => setDownloading(null))
  }

  return (
    <Card className="rcard">
      {/* Шапка называет документ один раз: плашка файла под ней повторяла
          то же имя и тот же тип — в ленте это читалось как два документа. */}
      <CardHead title={card.title} meta={kind} {...(runId != null ? { runId } : {})} />

      <div className="draft__clauses">
        {card.clauses.map((c, i) => (
          // Ключ по позиции: пункты идут по порядку и не переставляются, а
          // одинаковый текст у двух пунктов — обычное дело в шаблоне.
          <p key={`${c.number ?? i}-${i}`} className="draft__clause">
            {c.number != null ? <span className="draft__number">{c.number}</span> : null}
            <span>{c.text}</span>
          </p>
        ))}
        {card.clauses.length === 0 ? <p className="muted">Пунктов не получилось.</p> : null}
      </div>

      {card.placeholders.length > 0 ? (
        <>
          <Divider />
          <div>
            <Caps>Подставить самому · {card.placeholders.length}</Caps>
            <ol className="doc__questions">
              {card.placeholders.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ol>
          </div>
        </>
      ) : null}

      <Divider />

      <div>
        <Caps>Чего не хватает · {card.missing.length}</Caps>
        {/* Пустой список значит разное в зависимости от того, состоялась ли
            проверка. Не различать их — выдавать непроверенное за проверенное,
            и `checked` заведён в ядре ровно для этого. */}
        {!card.checked ? (
          <p className="doc__missing-none">
            Полноту проверить было некому: независимого поставщика не нашлось. Пустой список ниже не значит,
            что всё на месте.
          </p>
        ) : card.missing.length === 0 ? (
          <p className="doc__missing-none">Существенные условия на месте — я проверил документ вторым проходом.</p>
        ) : null}

        {card.missing.length > 0 ? (
          <div className="doc__missing">
            {card.missing.map((m) => (
              <p key={m}>{m}</p>
            ))}
          </div>
        ) : null}
      </div>

      <Divider />

      <div className="doc__actions">
        <button type="button" className="btn btn--primary" disabled={downloading != null} onClick={download('docx')}>
          Скачать DOCX
        </button>
        <button type="button" className="btn btn--ghost" disabled={downloading != null} onClick={download('pdf')}>
          Скачать PDF
        </button>
      </div>

      <CardNote note={note} />

      <p className="result__disclaimer">{card.disclaimer}</p>
    </Card>
  )
}
